import { format, setHours } from "date-fns";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, generatePath, useLocation } from "react-router-dom";
import { Button, Dropdown, Icon, Popup } from "semantic-ui-react";

import { Can, ConditionalWrap, DATE_FORMATS_FNS, DBB, DatePicker, Editable, InterventionModal, PSA, PlanIT, RDW, ROB, Recalls, useCan } from "components";
import { Element } from "components/ReactScroll/index";
import { ACL_ACTIVITIES, ACL_RESOURCES, CUSTOMER_TYPES, DMS_CAPABILITIES, STATUS_IDENTIFIERS } from "models";
import { CopyContent, Logs, OCWButton, PonOvis, ThreeDepartmentOverview, isPonOcwAvailable } from "modules/AppointmentDetails/components";
import "modules/AppointmentDetails/components/CarDetails/CarDetails.scss";
import { CarTyresModal, CustomerInfo, XentryStatus } from "modules/AppointmentDetails/components/CarDetails/components";
import CrossIconImage from "modules/AppointmentDetails/components/CarDetails/cross-icon.svg";
import {
  useAppointmentUpdateMileagesMutation,
  useCustomerOwnersQuery,
  useTextOverflow,
  useUpdateAppointmentCustomerOwnerMutation
} from "modules/AppointmentDetails/components/CarDetails/hooks";
import { useAppointmentDatesMutation, useAppointmentDealer, useAppointmentLocation, useCarBrandsQuery, useLiveUsers } from "modules/AppointmentDetails/hooks";
import { PATHS } from "router/paths";
import { canWriteBackCustomerData, isCarReadyOrQualityCheckDoneOrCanceled, isWorkNotStarted } from "utils/appointmentUtils";
import { getUserNameInitials, isAutolineLocation } from "utils/common";
import { formatDate, toDate } from "utils/dateHelperFunctions";

import type { DropdownItemProps, SectionMenu } from "components";
import type { Appointment } from "models";
import type { AddInterventionPayload } from "modules/AppointmentDetails/hooks";

type CarDetailsProps = {
  isExpanded: boolean;
  appointment: Appointment;
  sectionMenu: SectionMenu;
  handleCancelAppointment: () => void;
};

export const CarDetails = ({ isExpanded, appointment, handleCancelAppointment, sectionMenu }: CarDetailsProps) => {
  const { t } = useTranslation();
  const location = useLocation();

  const [showCardDetails, setShowCardDetails] = useState(isExpanded);
  const [showTyreModal, setShowTyreModal] = useState(false);
  const [interventionModalData, setInterventionModalData] = useState<AddInterventionPayload | null>(null);

  const appointmentLocation = useAppointmentLocation(appointment);
  const appointmentDealer = useAppointmentDealer(appointment);
  const { mutate: updateMileages } = useAppointmentUpdateMileagesMutation(appointment.id);
  const { mutate: updateAppointmentDates } = useAppointmentDatesMutation(appointment.id);

  const { users } = useLiveUsers(appointment.id);
  const canBrowseCarsPage = useCan("browse", "appointments-car-page");
  const canUpdateAppointments = useCan("update", "appointments-mileages");
  const canUpdateCustomerOwner = useCan("update", "appointments-customer-owner");
  const nextDate: Date | null = appointment.next_date && !appointment.next_date.startsWith("0001-01-01T00:00:00") ? toDate(appointment.next_date) : null;

  const { data: customerOwnersData } = useCustomerOwnersQuery();
  const { mutateAsync: updateCustomerOwner } = useUpdateAppointmentCustomerOwnerMutation();
  const customerOwnerOptions: DropdownItemProps[] =
    customerOwnersData?.map(({ id, first_name, last_name }) => ({ key: id, value: id, text: `${first_name} ${last_name}` })) || [];
  const { data: carBrands = [] } = useCarBrandsQuery();

  const isOcwButtonVisible = isPonOcwAvailable(carBrands, appointment.vin_nr, appointmentLocation, appointmentDealer);

  useEffect(() => {
    setShowCardDetails(isExpanded);
  }, [isExpanded]);

  const recallsIds = useMemo(() => {
    return appointment.interventions?.reduce<string[]>((acc, curr) => {
      const recallIds = curr.pin_history?.map(pin => pin.recalls_id || pin.pon_recall_id || pin.rdw_recall_id).filter((id): id is string => Boolean(id));
      if (recallIds) acc.push(...recallIds);
      return acc;
    }, []);
  }, [appointment]);

  const handleCustomerOwnerChange = async (userId: number) => updateCustomerOwner({ appointment_id: appointment.id, user_id: userId });
  const toggleCardDetails = () => setShowCardDetails(p => !p);

  const handleCurrentKmUpdate = (current_km: string) => updateMileages({ current_km: Number(current_km) });
  const handleNextKmUpdate = (next_km: string) => updateMileages({ next_km: Number(next_km) });

  const handleChangeNextKmDate = (...dates: (Date | null)[]) => {
    const [date] = dates;
    if (!date) return;

    updateAppointmentDates({ next_date: format(setHours(date, 12), DATE_FORMATS_FNS.ISOFormat) });
  };
  const handleCurrentKmValidation = (currentKm: string) => {
    return appointment.next_km === 0 || Number(currentKm) <= Number(appointment.next_km);
  };

  const handleNextKmValidation = (nextKm: string) => Number(nextKm) >= Number(appointment.current_km || 0);
  const handleCloseConvertRecallToIntervention = () => setInterventionModalData(null);
  const carMakeModel = `${appointment.car?.make || ""} ${appointment.car?.model || ""}`.trim();

  const carMakeModelRef = useRef<HTMLSpanElement>(null);
  const hasCarMakeModelOverflow = useTextOverflow(carMakeModelRef);

  return (
    <>
      <Element className="DetailsCards-container" id={sectionMenu.id}>
        <div className="row">
          <Icon className={sectionMenu.icon} />
          <h3>{sectionMenu.title}</h3>
          {<Icon className={`chevron ${showCardDetails ? "down" : "right"} pointer`} onClick={toggleCardDetails} />}
          <div className="full end">
            {appointmentLocation?.is_three_departments_enabled && <ThreeDepartmentOverview appointment={appointment} />}
            {isWorkNotStarted(appointment.appointment_status_identifier) && (
              <Can I="create" the="appointments-cancel">
                <Button className="-appointment-status icon cancel-appointment" size="small" onClick={handleCancelAppointment}>
                  <img src={CrossIconImage} />
                </Button>
              </Can>
            )}

            {users.map((user, index) => {
              const fullName = `${user.first_name || ""} ${user.last_name || ""}`.trim();
              return (
                <Popup
                  key={index}
                  content={fullName}
                  trigger={
                    user.profile_picture ? (
                      <img src={user.profile_picture} className="active-user" />
                    ) : (
                      <div className="active-user" key={index}>
                        {getUserNameInitials(fullName)}
                      </div>
                    )
                  }
                />
              );
            })}
          </div>
        </div>
        <div className={showCardDetails ? "CarDetails" : "hide"}>
          {appointment.car?.profile_picture && (
            <div className="car-image-container">
              <img
                src={appointment.car.profile_picture}
                className="car-image"
                onError={e => {
                  if (e.currentTarget.parentElement) e.currentTarget.parentElement.style.display = "none";
                }}
              />
            </div>
          )}
          <div className="car-container">
            <div className="car-header">
              <div className="car-items">
                <Icon className="car mirrors" />
                <p>{t("v8_car", "Car")}</p>

                <div className="car-items-reg-info ">
                  {appointment.car?.reg_nr && (
                    <CopyContent content={appointment.car.reg_nr}>
                      <ConditionalWrap
                        condition={Boolean(canBrowseCarsPage && appointment.car.id)}
                        wrap={children =>
                          appointment.car && appointment.car.id ? (
                            <Link
                              to={{ pathname: generatePath(PATHS.CAR_DETAILS, { id: appointment.car.id }), search: `?from=${location.pathname}` }}
                              className="info-link"
                              replace
                            >
                              {children}
                            </Link>
                          ) : (
                            children
                          )
                        }
                      >
                        <strong>{appointment.car.reg_nr}</strong>
                      </ConditionalWrap>
                    </CopyContent>
                  )}

                  <Popup
                    disabled={!hasCarMakeModelOverflow}
                    content={
                      <>
                        <span>
                          <strong>{t("v8_make", "Make")}:</strong> {appointment.car?.make || "-"}
                        </span>
                        <br />
                        <span>
                          <strong>{t("v8_model", "Model")}:</strong> {appointment.car?.model || "-"}
                        </span>
                      </>
                    }
                    trigger={
                      <span ref={carMakeModelRef} className="car-items-make-model ellipsis">
                        <strong>{carMakeModel}</strong>
                      </span>
                    }
                  />
                </div>
              </div>

              <div className="car-items">
                {(appointment.car?.in_storage_tyres || appointment.car?.on_car_tyres) && (
                  <Button className="tyres-overview-button -appointment-status icon" size="small" onClick={() => setShowTyreModal(true)}>
                    <Icon className="tire black" />
                  </Button>
                )}

                {appointment.appointment_status_identifier !== STATUS_IDENTIFIERS.CanceledStatus && (
                  <>
                    {appointmentLocation?.dbb_enabled && appointmentLocation.dbb_user && (
                      <Can I="update" the="dbb">
                        <DBB appointment={appointment} />
                      </Can>
                    )}

                    {isOcwButtonVisible && <OCWButton appointmentId={appointment.id} ocwEnabled={Boolean(appointment.car?.ocw_enabled)} />}

                    <Can I={ACL_ACTIVITIES.List} the={ACL_RESOURCES.ServiceBoxWOWidget}>
                      <PSA id={appointment.id} type="appointment" location={appointmentLocation} />
                    </Can>
                    <Can I="list" the="recalls">
                      <Recalls
                        vin={appointment.car?.vin_nr}
                        recallsIdsToDisable={recallsIds}
                        appointmentId={appointment.id}
                        showClosedRecalls
                        selectOnly={false}
                        dealerLocationId={appointmentLocation?.id}
                        onOpenConvertRecallToIntervention={setInterventionModalData}
                        licensePlate={appointment.car?.reg_nr}
                        hasRecallsData={appointment.has_recalls_data}
                      />
                    </Can>
                    {appointmentDealer?.is_pon_enabled && (
                      <Can I="list" the="pon-vehicle-ovis">
                        <PonOvis car_id={appointment.car?.id} hasOvisData={appointment.has_ovis_data} />
                      </Can>
                    )}
                    <Can I="list" the="car-rdw">
                      <RDW regNumber={appointment.car?.reg_nr} country={appointmentLocation?.country} />
                    </Can>
                    <Can I={ACL_ACTIVITIES.List} the={ACL_RESOURCES.XentryJobs}>
                      <XentryStatus
                        carBrands={carBrands}
                        vinNr={appointment.vin_nr}
                        appointmentId={appointment.id}
                        isXentryEnabled={appointmentDealer?.is_xentry_enabled}
                        subOutletId={appointmentLocation?.xentry_sub_outlet_id}
                      />
                    </Can>
                    {appointmentLocation?.planit_planning_establishment_id && (
                      <Can I="list" the="planit-appointment">
                        <PlanIT appointment_id={appointment.id} hasPlanITData={appointment.has_plan_it_data} />
                      </Can>
                    )}
                    <Can I="list" the="robnet-contract">
                      <ROB car_id={appointment.car_id} dealer_location_id={appointment.car?.dealer_location_id} />
                    </Can>
                  </>
                )}
                <Can I={ACL_ACTIVITIES.List} the={ACL_RESOURCES.AppointmentsLogs}>
                  <Logs appointment_id={Number(appointment.id)} />
                </Can>
              </div>
            </div>
            <div className="car-info">
              <div className="info-data">
                <p>{t("v8_vin_chassis", "Vin / Chassis") + " #"}</p>
                <CopyContent content={appointment.car?.vin_nr ?? ""}>
                  <p className="car-info-value">{appointment.car?.vin_nr}</p>
                </CopyContent>
              </div>

              {appointment.car?.warranty_date_end && (
                <div className="info-data">
                  <p>{t("v8_warranty_end_date", "Warranty End ")}</p>
                  <p className="car-info-value">{formatDate(appointment.car.warranty_date_end, DATE_FORMATS_FNS.dateMonthYear)}</p>
                </div>
              )}

              {appointment.car?.dms_registration && (
                <div className="info-data">
                  <p>{t("v8_dms_registration", "DMS registration")}</p>
                  <p className="car-info-value">{formatDate(appointment.car.dms_registration, DATE_FORMATS_FNS.dateMonthYear)}</p>
                </div>
              )}

              {appointment.car?.rdw_first_registration && (
                <div className="info-data">
                  <p>{t("v8_first_registration_date", "First Registration")}</p>
                  <p className="car-info-value">{formatDate(appointment.car.rdw_first_registration, DATE_FORMATS_FNS.dateMonthYear)}</p>
                </div>
              )}

              {appointment.car?.rdw_first_registration_nl && (
                <div className="info-data">
                  <p>{t("v8_first_registration_date_nl", "First registration NL")}</p>
                  <p className="car-info-value">{formatDate(appointment.car.rdw_first_registration_nl, DATE_FORMATS_FNS.dateMonthYear)}</p>
                </div>
              )}

              {appointment.fleet_number && (
                <div className="info-data">
                  <p>{t("v8_fleet_number", "Fleet Number")}</p>
                  <p className="car-info-value">{appointment.fleet_number}</p>
                </div>
              )}

              <div className="info-data">
                <p>{t("v8_current_km", "Current Km")}</p>
                <ConditionalWrap
                  condition={Boolean(appointment.current_km)}
                  wrap={children => <CopyContent content={String(appointment.current_km)}>{children}</CopyContent>}
                >
                  <Editable
                    isNumberInput
                    valueClassName="car-info-value"
                    value={String(appointment.current_km)}
                    onSubmit={handleCurrentKmUpdate}
                    disabled={!canUpdateAppointments || isCarReadyOrQualityCheckDoneOrCanceled(appointment.appointment_status_identifier)}
                    onValidation={handleCurrentKmValidation}
                    errorMessage={t("v8_current_km_not_greater_than_next_km", "Current km cannot be greater than next km")}
                  />
                </ConditionalWrap>
              </div>

              {appointmentLocation?.apk_visible && (
                <div className="info-data">
                  <p>{t("v8_apk", "APK")}</p>
                  {appointment.car?.apk_date_dms && <p className="car-info-value">{formatDate(appointment.car.apk_date_dms, DATE_FORMATS_FNS.dateMonthYear)}</p>}
                </div>
              )}

              {!appointmentLocation?.hide_next_km_and_next_date && (
                <div className="info-data">
                  <p>{t("v8_next_km", "Next Km")}</p>
                  <Editable
                    isNumberInput
                    valueClassName="car-info-value"
                    value={String(appointment.next_km)}
                    onSubmit={handleNextKmUpdate}
                    disabled={!canUpdateAppointments || isCarReadyOrQualityCheckDoneOrCanceled(appointment.appointment_status_identifier)}
                    onValidation={handleNextKmValidation}
                    errorMessage={t("v8_next_km_not_less_than_current_km", "Next km cannot be less than current km")}
                  />
                </div>
              )}

              {!appointmentLocation?.hide_next_km_and_next_date && (
                <div className="info-data AppointmentDate">
                  <p>{t("v8_next_date", "Next Date")}</p>
                  <DatePicker
                    date={nextDate}
                    onDateChange={handleChangeNextKmDate}
                    withPortal
                    showMonthDropdown
                    showYearDropdown
                    customDatePickerStyle={nextDate ? "" : "extendedCarDetailsDatePicker"}
                  />
                </div>
              )}

              {appointmentLocation?.dms_capability_ids?.includes(DMS_CAPABILITIES.ImportMileage) && (
                <div className="info-column">
                  <p>{t("v8_dms_km", "DMS Km")}</p>
                  <p className="car-info-value">{appointment.dms_km}</p>
                </div>
              )}

              {appointment.car?.engine_number && (
                <div className="info-column">
                  <p>{t("v8_engine", "Engine") + " #"}</p>
                  <p className="car-info-value">{appointment.car.engine_number}</p>
                </div>
              )}
            </div>

            <div className="customer-details">
              <div className="car-header">
                <div className="car-items">
                  <Icon className="user person" />
                  <p>{t("v8_customer", "Customer")}</p>
                </div>
              </div>
              <div className="customer-details-info">
                <div className="info-data-container">
                  {appointmentLocation?.dp_driver_visible && (
                    <div className="info-data">
                      <div>
                        <CustomerInfo appointment={appointment} customerType={CUSTOMER_TYPES.Driver} />
                      </div>
                    </div>
                  )}
                  {appointmentLocation?.dp_contractor_visible && (
                    <div className="info-data">
                      <div>
                        <CustomerInfo appointment={appointment} customerType={CUSTOMER_TYPES.Contractor} />
                      </div>
                    </div>
                  )}
                  {appointmentLocation?.dp_owner_visible && (
                    <div className="info-data">
                      <div>
                        <CustomerInfo appointment={appointment} customerType={CUSTOMER_TYPES.Owner} />
                      </div>
                    </div>
                  )}
                </div>
                <Can I="list" the="users">
                  <Dropdown
                    disabled={!canUpdateCustomerOwner || !appointment.car_in_shop_edited || isAutolineLocation(appointmentLocation)}
                    options={customerOwnerOptions}
                    selectOnBlur={false}
                    onChange={(_, data) => typeof data.value === "number" && handleCustomerOwnerChange(data.value)}
                    className="selection"
                    trigger={
                      <div className="customer-owner-container">
                        <div className="customer-owner-icon">CO</div>
                        <div>
                          <span className={`customer-owner-name-text ${!appointment.customer_owner_name && "customer-owner-name-placeholder"}`}>
                            {appointment.customer_owner_name || t("v8_select_customer_owner", "Select customer owner")}
                          </span>
                        </div>
                      </div>
                    }
                  />
                </Can>
              </div>
            </div>
          </div>
        </div>
      </Element>

      <InterventionModal
        isCarRecallPinType
        location={appointmentLocation}
        dealer={appointmentDealer}
        intervention={interventionModalData}
        open={interventionModalData !== null}
        appointmentId={appointment.id}
        carId={appointment.car_id}
        onClose={handleCloseConvertRecallToIntervention}
        canWriteBackCustomerData={canWriteBackCustomerData(appointmentLocation)}
      />

      {appointment.car && <CarTyresModal appointment={appointment} open={showTyreModal} onClose={() => setShowTyreModal(false)} />}
    </>
  );
};
